// src/hooks/useBuyerPersonaImage.js
import { useState, useEffect, useRef } from 'react';
import buyerPersonaService from '../helpers/buyerPersonaService';
import { BUYER_PERSONA_CONFIG } from '../config/buyerPersonaConfig';

const { UI, IMAGE_STATUS } = BUYER_PERSONA_CONFIG;

export const useBuyerPersonaImage = () => {
    const [imageStatus, setImageStatus] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState(null);

    const pollingIntervalRef = useRef(null);
    const timeoutRef = useRef(null);

    // Verificar estado de la imagen
    const checkImageStatus = async () => {
        try {
            console.log('🔍 Solicitando estado de imagen al backend...');
            const response = await buyerPersonaService.getImageStatus();
            console.log('📥 Respuesta del backend:', response);

            if (response) {
                // Buscar la información de imagen en diferentes ubicaciones posibles
                const status = response.status || response.imageStatus || response.result?.status;
                const imageUrl = response.imageUrl || response.result?.imageUrl;
                const imageReady = response.imageReady;
                const imageGeneratedAt = response.imageGeneratedAt;

                console.log('🔄 Procesando respuesta:', {
                    status,
                    imageUrl,
                    imageReady,
                    imageGeneratedAt
                });

                // Si no hay status pero hay imageReady, usar eso
                const finalStatus = status || (imageReady ? 'completed' : 'pending');
                console.log('📊 Estado final:', finalStatus);

                setImageStatus(finalStatus);

                // Construir URL de imagen
                let fullImageUrl = null;
                if (imageUrl) {
                    if (imageUrl.startsWith('http')) {
                        fullImageUrl = imageUrl;
                        console.log('🔗 Usando URL completa:', fullImageUrl);
                    } else {
                        // Asegurarnos de que la URL sea absoluta
                        const baseUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development'
                            ? 'http://localhost:3000'
                            : 'https://api.petitprime.com');

                        // Asegurarnos de que la URL tenga el formato correcto
                        const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
                        fullImageUrl = `${baseUrl}${cleanImageUrl}`;
                        console.log('🔨 URL construida:', {
                            baseUrl,
                            cleanImageUrl,
                            fullImageUrl
                        });
                    }

                    // Verificar si la imagen existe
                    try {
                        console.log('🔍 Verificando accesibilidad de la imagen:', fullImageUrl);
                        const response = await fetch(fullImageUrl, { method: 'HEAD' });
                        if (!response.ok) {
                            console.warn('⚠️ Imagen no accesible:', fullImageUrl, 'Status:', response.status);
                            fullImageUrl = null;
                        } else {
                            console.log('✅ Imagen verificada y accesible');
                        }
                    } catch (error) {
                        console.warn('❌ Error verificando imagen:', error);
                        fullImageUrl = null;
                    }
                } else {
                    console.log('⚠️ No se recibió URL de imagen del backend');
                }

                setImageUrl(fullImageUrl);
                setError(null);

                // Si no hay imagen pero el estado es completed, iniciar polling
                if (!fullImageUrl && finalStatus === 'completed') {
                    console.log('🔄 Iniciando polling por falta de imagen en estado completed');
                    startPolling();
                }

                return {
                    status: finalStatus,
                    imageUrl: fullImageUrl,
                    success: true
                };
            } else {
                console.warn('⚠️ Respuesta vacía del backend');
                throw new Error('Error al verificar el estado de la imagen');
            }
        } catch (error) {
            console.error('❌ Error en checkImageStatus:', error);
            // Si el backend no está listo, no mostrar error
            if (error.message && error.message.includes('No tienes un buyer persona creado')) {
                console.log('ℹ️ No hay buyer persona creado aún');
                setError(null);
            } else {
                setError('Error al verificar el estado de la imagen');
            }
            return null;
        }
    };

    // Iniciar polling automático
    const startPolling = () => {
        if (isPolling) return; // Evitar múltiples polling simultáneos

        setIsPolling(true);
        setError(null);



        // Verificar estado inmediatamente
        checkImageStatus();

        // Configurar polling cada 3 segundos
        pollingIntervalRef.current = setInterval(async () => {
            const status = await checkImageStatus();
            if (status && status.success && (status.status === IMAGE_STATUS.COMPLETED || status.status === IMAGE_STATUS.FAILED || status.status === 'completed')) {
                stopPolling();
            }
        }, UI.POLLING_INTERVAL);

        // Timeout después de 2 minutos
        timeoutRef.current = setTimeout(() => {
            stopPolling();
            setError('Tiempo de espera agotado para la generación de la imagen');
        }, UI.POLLING_TIMEOUT);
    };

    // Detener polling
    const stopPolling = () => {
        setIsPolling(false);
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // Regenerar imagen
    const regenerateImage = async () => {
        try {
            setError(null);
            const response = await buyerPersonaService.regenerateImage();
            if (response.success) {
                setImageStatus(IMAGE_STATUS.PENDING);
                setImageUrl(null);
                startPolling();
                return response;
            } else {
                throw new Error(response.message || 'Error al regenerar la imagen');
            }
        } catch (error) {
            console.error('Error regenerating image:', error);
            // Si el backend no está listo, mostrar mensaje apropiado
            if (error.message && error.message.includes('No tienes un buyer persona creado')) {
                setError('El módulo de Buyer Persona no está disponible temporalmente');
            } else {
                setError('Error al regenerar la imagen');
            }
            throw error;
        }
    };

    // Obtener imagen como blob con autorización
    const getImageAsBlob = async (imageFileName) => {
        try {
            const response = await buyerPersonaService.getImage(imageFileName);
            if (response && response.success && response.imageUrl) {
                // Convertir la imagen a blob y crear URL de datos
                const blobResponse = await fetch(response.imageUrl, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (blobResponse.ok) {
                    const blob = await blobResponse.blob();
                    return URL.createObjectURL(blob);
                }
            }
            return null;
        } catch (error) {
            console.error('Error getting image as blob:', error);
            return null;
        }
    };

    // Obtener mensaje de estado
    const getStatusMessage = () => {
        switch (imageStatus) {
            case IMAGE_STATUS.PENDING:
                return '🔄 Preparando generación de imagen...';
            case IMAGE_STATUS.GENERATING:
                return '🎨 Generando retrato con IA...';
            case IMAGE_STATUS.COMPLETED:
                return '✅ Imagen generada exitosamente';
            case IMAGE_STATUS.FAILED:
                return '❌ Error generando imagen';
            default:
                return '✅ Buyer persona generado exitosamente';
        }
    };

    // Obtener color de estado
    const getStatusColor = () => {
        switch (imageStatus) {
            case IMAGE_STATUS.PENDING:
            case IMAGE_STATUS.GENERATING:
                return 'text-blue-600';
            case IMAGE_STATUS.COMPLETED:
                return 'text-green-600';
            case IMAGE_STATUS.FAILED:
                return 'text-red-600';
            default:
                return 'text-gray-600';
        }
    };

    // Limpiar al desmontar
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);

    return {
        // Estado
        imageStatus,
        imageUrl,
        isPolling,
        error,

        // Acciones
        checkImageStatus,
        startPolling,
        stopPolling,
        regenerateImage,

        // Utilidades
        getStatusMessage,
        getStatusColor
    };
}; 